import React, { useEffect, useState, useRef, useCallback } from 'react';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { db } from '../firebase/firebase';
import { useNavigate } from 'react-router-dom';

/** Inline preview clip: no native controls (avoids browser ⋮ / PiP chrome); tap toggles play/pause. */
function ActivityPreviewInlineVideo({ src, previewPlayingVideoRef }) {
  const videoRef = useRef(null);
  const [showPlayAffordance, setShowPlayAffordance] = useState(true);

  const toggle = useCallback(
    (e) => {
      e.stopPropagation();
      const v = videoRef.current;
      if (!v) return;
      if (v.paused) {
        if (previewPlayingVideoRef.current && previewPlayingVideoRef.current !== v) {
          previewPlayingVideoRef.current.pause();
        }
        previewPlayingVideoRef.current = v;
        void v.play();
      } else {
        v.pause();
      }
    },
    [previewPlayingVideoRef],
  );

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onPlay = () => {
      if (previewPlayingVideoRef.current && previewPlayingVideoRef.current !== v) {
        previewPlayingVideoRef.current.pause();
      }
      previewPlayingVideoRef.current = v;
      setShowPlayAffordance(false);
    };
    const onPause = () => {
      if (previewPlayingVideoRef.current === v) {
        previewPlayingVideoRef.current = null;
      }
      setShowPlayAffordance(true);
    };
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    return () => {
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
    };
  }, [src, previewPlayingVideoRef]);

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        borderRadius: '8px',
        overflow: 'hidden',
        background: '#0f172a',
        cursor: 'pointer',
      }}
      onClick={toggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle(e);
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={showPlayAffordance ? 'Play video' : 'Pause video'}
    >
      <video
        ref={videoRef}
        src={src}
        playsInline
        preload="metadata"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
          pointerEvents: 'none',
        }}
      />
      {showPlayAffordance && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(180deg, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.38) 100%)',
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              width: 'clamp(36px, 3vw, 44px)',
              height: 'clamp(36px, 3vw, 44px)',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.95)',
              color: 'var(--branding-app-primary, #2563eb)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 'clamp(14px, 1.2vw, 18px)',
              boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
              paddingLeft: '3px',
            }}
            aria-hidden
          >
            ▶
          </div>
        </div>
      )}
    </div>
  );
}

const ActivityPreview = ({ projectId, onClick }) => {
  const [latestActivities, setLatestActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [voiceNotes, setVoiceNotes] = useState({});
  const [playingVoiceNote, setPlayingVoiceNote] = useState(null);
  const [audioElements, setAudioElements] = useState({});
  const [optimizedImages, setOptimizedImages] = useState({});
  const [currentImageIndex, setCurrentImageIndex] = useState({}); // Track current image index per activity
  const previewPlayingVideoRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!projectId) return;

    const unsubscribe = onSnapshot(
      query(
        collection(db, `projects/${projectId}/work_uploads`),
        orderBy('timestamp', 'desc'),
        limit(8)
      ),
      (snapshot) => {
        if (!snapshot.empty) {
          const activities = snapshot.docs.map(doc => {
            const data = doc.data();
            // Handle both new format (imageUrls array) and old format (imageUrl string)
            let imageUrls = [];
            if (data.imageUrls && Array.isArray(data.imageUrls) && data.imageUrls.length > 0) {
              // New format: array
              imageUrls = data.imageUrls.map(url => url.trim()).filter(url => url.length > 0);
            } else if (data.imageUrl && typeof data.imageUrl === 'string' && data.imageUrl.length > 0) {
              // Old format: comma-separated string
              imageUrls = data.imageUrl.split(',').map(url => url.trim()).filter(url => url.length > 0);
            }

            let videoUrls = [];
            if (data.videoUrls && Array.isArray(data.videoUrls) && data.videoUrls.length > 0) {
              videoUrls = data.videoUrls.map((url) => String(url).trim()).filter((url) => url.length > 0);
            }

            const mediaSlideCount = imageUrls.length + videoUrls.length;

            const commentText =
              typeof data.comment === 'string' ? data.comment.trim() : '';
            return {
              id: doc.id,
              type: 'activity',
              title: data.teamName || 'Unknown team',
              subtitle: commentText,
              timestamp: data.timestamp?.toDate(),
              hasImage: imageUrls.length > 0,
              imageCount: imageUrls.length,
              videoUrls,
              videoCount: videoUrls.length,
              mediaSlideCount,
              hasComment: commentText.length > 0,
              userId: data.userId,
              imageUrl: imageUrls[0] || null,  // First image for thumbnail
              imageUrls, // All images
            };
          });
          setLatestActivities(activities);
        } else {
          setLatestActivities([]);
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching activity:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [projectId]);

  // Optimize images for preview
  const optimizeImage = (imageUrl, maxSize = 160) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Calculate dimensions to fit within maxSize while maintaining aspect ratio
        let { width, height } = img;
        if (width > height) {
          height = (height * maxSize) / width;
          width = maxSize;
        } else {
          width = (width * maxSize) / height;
          height = maxSize;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw resized image
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to optimized data URL
        const optimizedUrl = canvas.toDataURL('image/jpeg', 0.8);
        resolve(optimizedUrl);
      };
      
      img.onerror = () => {
        // If optimization fails, return original URL
        resolve(imageUrl);
      };
      
      img.src = imageUrl;
    });
  };

  // Fetch voice notes and optimize images
  useEffect(() => {
    const fetchVoiceNotesAndOptimizeImages = async () => {
      if (!projectId) return;
      
      const voiceNotesMap = {};
      const optimizedImagesMap = {};
      const storage = getStorage();
      
      for (const activity of latestActivities) {
        // Fetch voice notes
        if (activity.userId) {
          try {
            const voiceRef = ref(storage, `projects/${projectId}/work_uploads/${activity.userId}/voice_notes/${activity.id}.mp4`);
            const voiceUrl = await getDownloadURL(voiceRef);
            voiceNotesMap[activity.id] = voiceUrl;
          } catch (error) {
            // Voice note doesn't exist for this upload
          }
        }
        
        // Optimize all images for carousel (but prioritize first one)
        if (activity.imageUrls && activity.imageUrls.length > 0) {
          // Optimize first image immediately (for initial display)
          try {
            const optimizedUrl = await optimizeImage(activity.imageUrls[0]);
            optimizedImagesMap[`${activity.id}_0`] = optimizedUrl;
          } catch (error) {
            optimizedImagesMap[`${activity.id}_0`] = activity.imageUrls[0];
          }
          
          // Optimize remaining images in background (lazy load)
          activity.imageUrls.slice(1).forEach((url, index) => {
            optimizeImage(url)
              .then(optimizedUrl => {
                setOptimizedImages(prev => ({
                  ...prev,
                  [`${activity.id}_${index + 1}`]: optimizedUrl
                }));
              })
              .catch(() => {
                setOptimizedImages(prev => ({
                  ...prev,
                  [`${activity.id}_${index + 1}`]: url
                }));
              });
          });
        } else if (activity.imageUrl) {
          // Fallback for old format
          try {
            const optimizedUrl = await optimizeImage(activity.imageUrl);
            optimizedImagesMap[activity.id] = optimizedUrl;
          } catch (error) {
            optimizedImagesMap[activity.id] = activity.imageUrl;
          }
        }
      }
      
      setVoiceNotes(voiceNotesMap);
      setOptimizedImages(optimizedImagesMap);
    };

    if (latestActivities.length > 0) {
      fetchVoiceNotesAndOptimizeImages();
    }
  }, [latestActivities, projectId]);

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const diffInMinutes = Math.floor((now - timestamp) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const handleVoiceNotePlay = (activityId, voiceUrl) => {
    // Stop any currently playing voice note
    if (playingVoiceNote && playingVoiceNote !== activityId) {
      const currentAudio = audioElements[playingVoiceNote];
      if (currentAudio) {
        currentAudio.pause();
        // Don't reset currentTime - keep position for potential resume
      }
      setPlayingVoiceNote(null);
    }

    if (playingVoiceNote === activityId) {
      // Pause current voice note - keep current position
      const audio = audioElements[activityId];
      if (audio) {
        audio.pause();
        setPlayingVoiceNote(null);
      }
    } else {
      // Play new voice note or resume existing one
      let audio = audioElements[activityId];
      
      if (!audio) {
        // Create new audio element if it doesn't exist
        audio = new Audio(voiceUrl);
        audio.addEventListener('ended', () => {
          setPlayingVoiceNote(null);
        });
        setAudioElements(prev => ({ ...prev, [activityId]: audio }));
      }
      
      // Resume from current position or start from beginning
      audio.play();
      setPlayingVoiceNote(activityId);
    }
  };

  if (loading) {
    return (
      <div style={{
        height: '120px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--branding-app-surface-panel-hint)',
        fontSize: '14px'
      }}>
        Loading...
      </div>
    );
  }

  if (!latestActivities || latestActivities.length === 0) {
    return (
      <div style={{
        height: '120px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--branding-app-surface-panel-hint)',
        fontSize: '14px'
      }}>
        No recent activity
      </div>
    );
  }

  return (
    <div 
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        background: 'transparent',
        /* WhitePanel already pads the card; keep vertical rhythm only so media uses more width */
        padding: '6px 0 10px',
        width: '100%',
        boxSizing: 'border-box',
        transition: 'all 0.2s ease'
      }}
    >
      <div style={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        paddingRight: '4px'
      }}>
        {latestActivities.map((activity, index) => {
          const mediaIsHero = activity.mediaSlideCount > 0 && !activity.hasComment;
          const thumbSize = mediaIsHero
            ? 'clamp(108px, 28%, 200px)'
            : 'clamp(88px, 24%, 140px)';
          const displaySubtitle = activity.hasComment
            ? activity.subtitle
            : activity.mediaSlideCount > 0
              ? [
                  activity.imageCount > 0
                    ? `${activity.imageCount} photo${activity.imageCount === 1 ? '' : 's'}`
                    : null,
                  activity.videoCount > 0
                    ? `${activity.videoCount} video${activity.videoCount === 1 ? '' : 's'}`
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ') || 'Recent upload'
              : 'Open activity for details';
          return (
          <div 
            key={activity.id || index}
            style={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              width: '100%',
              minWidth: 0,
              gap: mediaIsHero ? '14px' : '12px', 
              marginBottom: index < latestActivities.length - 1 ? '16px' : '0',
              paddingBottom: index < latestActivities.length - 1 ? '16px' : '0',
              borderBottom: index < latestActivities.length - 1 ? '1px solid var(--branding-app-surface)' : 'none',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '12px',
              transition: 'background-color 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--branding-app-surface-panel)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            onClick={() => navigate(`/project/${projectId}/activity`)}
          >
            {/* Image Thumbnail or Icon with Carousel for Multiple Images */}
            <div style={{
              width: thumbSize,
              height: thumbSize,
              background: 'transparent',
              border: 'none',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              overflow: 'hidden',
              position: 'relative',
              boxShadow: mediaIsHero ? '0 4px 16px rgba(15, 23, 42, 0.12)' : '0 2px 8px rgba(0,0,0,0.06)',
            }}>
              {activity.mediaSlideCount > 0 ? (
                <div 
                  style={{ 
                    position: 'relative', 
                    width: '100%', 
                    height: '100%'
                  }}
                >
                  {(() => {
                    const slide = currentImageIndex[activity.id] || 0;
                    const isVideo = slide >= activity.imageUrls.length;
                    if (isVideo) {
                      const vIdx = slide - activity.imageUrls.length;
                      const vSrc = activity.videoUrls[vIdx];
                      return (
                        <ActivityPreviewInlineVideo
                          src={vSrc}
                          previewPlayingVideoRef={previewPlayingVideoRef}
                        />
                      );
                    }
                    return (
                      <img
                        src={optimizedImages[`${activity.id}_${slide}`] || (activity.imageUrls && activity.imageUrls[slide]) || activity.imageUrl}
                        alt="Work upload"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '8px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          border: '1px solid var(--branding-app-surface)',
                          imageRendering: 'auto',
                          transition: 'opacity 0.2s ease'
                        }}
                        loading="lazy"
                        decoding="async"
                      />
                    );
                  })()}
                  {activity.mediaSlideCount > 1 && (
                    <>
                      {/* Navigation Arrows */}
                      {/* Left Arrow */}
                      <div 
                            style={{
                              position: 'absolute',
                              left: '2px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              background: 'rgba(0, 0, 0, 0.6)',
                              color: '#fff',
                              width: 'clamp(18px, 1.2vw, 28px)',
                              height: 'clamp(18px, 1.2vw, 28px)',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 'clamp(9px, 0.7vw, 14px)',
                              cursor: 'pointer',
                              transition: 'background 0.2s ease',
                              zIndex: 2,
                              paddingLeft: '1px'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              const currentIndex = currentImageIndex[activity.id] || 0;
                              const prevIndex = currentIndex === 0 ? activity.mediaSlideCount - 1 : currentIndex - 1;
                              setCurrentImageIndex(prev => ({ ...prev, [activity.id]: prevIndex }));
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.8)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.6)';
                            }}
                            title="Previous image"
                          >
                            ◀
                          </div>
                          {/* Right Arrow */}
                          <div 
                            style={{
                              position: 'absolute',
                              right: '2px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              background: 'rgba(0, 0, 0, 0.6)',
                              color: '#fff',
                              width: 'clamp(18px, 1.2vw, 28px)',
                              height: 'clamp(18px, 1.2vw, 28px)',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 'clamp(9px, 0.7vw, 14px)',
                              cursor: 'pointer',
                              transition: 'background 0.2s ease',
                              zIndex: 2,
                              paddingLeft: '1px'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              const currentIndex = currentImageIndex[activity.id] || 0;
                              const nextIndex = (currentIndex + 1) % activity.mediaSlideCount;
                              setCurrentImageIndex(prev => ({ ...prev, [activity.id]: nextIndex }));
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.8)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.6)';
                            }}
                            title="Next image"
                          >
                            ▶
                          </div>
                      {/* Image Counter */}
                      <div style={{
                        position: 'absolute',
                        bottom: '4px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: 'rgba(0, 0, 0, 0.7)',
                        color: '#fff',
                        fontSize: 'clamp(9px, 0.6vw, 12px)',
                        fontWeight: '600',
                        padding: 'clamp(2px, 0.15vw, 4px) clamp(6px, 0.4vw, 8px)',
                        borderRadius: '4px',
                        lineHeight: '1.2',
                        whiteSpace: 'nowrap',
                        zIndex: 1
                      }}>
                        {(currentImageIndex[activity.id] || 0) + 1} / {activity.mediaSlideCount}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <span style={{
                  fontSize: '16px',
                  color: 'var(--branding-app-surface-panel-muted)'
                }}>
                  📝
                </span>
              )}
            </div>
            
            {/* Content */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: '14px',
                fontWeight: '600',
                color: 'var(--branding-app-surface-panel-fg)',
                marginBottom: '4px',
                lineHeight: '1.3',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {activity.title}
              </div>
              
              <div style={{
                fontSize: '13px',
                fontWeight: mediaIsHero ? '500' : '400',
                color: mediaIsHero
                  ? 'var(--branding-app-surface-panel-fg)'
                  : 'var(--branding-app-surface-panel-sub)',
                marginBottom: '8px',
                lineHeight: '1.35',
                overflow: 'hidden',
                ...(mediaIsHero
                  ? {
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      whiteSpace: 'normal',
                    }
                  : {
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }),
              }}
                title={displaySubtitle}
              >
                {displaySubtitle}
              </div>
              
              <div style={{
                fontSize: '12px',
                color: 'var(--branding-app-surface-panel-hint)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>{getTimeAgo(activity.timestamp)}</span>
                {voiceNotes[activity.id] && (
                  <button
                    style={{
                      background: 'var(--branding-app-primary)',
                      color: 'var(--branding-app-primary-fg)',
                      border: 'none',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      fontSize: '12px',
                      padding: 0,
                      lineHeight: 1,
                      transition: 'all 0.2s ease'
                    }}
                    title={playingVoiceNote === activity.id ? "Pause voice note" : "Play voice note"}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleVoiceNotePlay(activity.id, voiceNotes[activity.id]);
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'var(--branding-app-primary-hover)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'var(--branding-app-primary)';
                    }}
                  >
                    {playingVoiceNote === activity.id ? '⏸' : '▶'}
                  </button>
                )}
              </div>
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
};

export default ActivityPreview;
